import { useCallback, useState } from "react";

import { useLocation } from "react-router";

import { AxiosError } from "axios";
import { useDetailsStore } from "@/stores/useDetailsStore";
import {
  useIntegrationsTableStore,
  useTableStoreByRoute
} from "@/stores/useTableStore";

import useAxiosPrivate from "../../../api/hooks/useAxiosPrivate";
import { ALERT_STATUS } from "../../../context/alert/types";
import { GetTableitemDetailsProps, ROUTES } from "../../../shared";
import useRefreshTableData from "../../../shared/hooks/useRefreshTableData";
import useResponse from "../../../shared/hooks/useResponse";

const useTableItemDetails = () => {
  const { handleResponse } = useResponse();
  const { getData } = useAxiosPrivate();
  const location = useLocation();
  const { refreshTableData } = useRefreshTableData();

  // Get the appropriate table store based on current route
  const { selectedRows, setSelectedRows, setParams, setDetailsData } =
    useTableStoreByRoute(location.pathname as ROUTES);

  const { isLoading, setIsLoading } = useDetailsStore();

  const [processingItemId, setProcessingItemId] = useState<
    string | number | null
  >(null);

  const fetchAndUpdateItemDetails = useCallback(
    async (
      itemId: string | number,
      updateTableRowParams: boolean,
      successMessage?: string,
      rowData?: any,
      urlKey?: string | null
    ) => {
      const getDataEndpoint = () => {
        const defaultUrl = `${location.pathname}/${itemId}`;

        if (location.pathname === ROUTES.Integrations) {
          if (rowData) {
            return `${location.pathname}/${rowData.type}/${itemId}`;
          }

          if (urlKey) {
            return `${location.pathname}/${urlKey}/${itemId}`;
          }

          const integrationsStore = useIntegrationsTableStore.getState();

          const integrationsDetailsItem = integrationsStore.detailsData;

          return integrationsDetailsItem
            ? `${location.pathname}/${integrationsDetailsItem.type}/${itemId}`
            : defaultUrl;
        }

        return defaultUrl;
      };

      const dataEndpoint = getDataEndpoint();

      await getData(dataEndpoint).then(resp => {
        // Update the full details data object in the store (not persisted)
        setDetailsData(resp.data);

        // Update only the ID in params (persisted to storage)

        if (updateTableRowParams) {
          const hasUrlKey = Boolean(resp.data.type);

          setParams({
            selectedTableItem: resp.data.id,
            desktopDetailsSection: true,
            ...(hasUrlKey && { selectedTableItemUrlKey: resp.data.type })
          });
        }

        if (successMessage) {
          handleResponse(ALERT_STATUS.Success, successMessage);
        }
      });
    },
    [getData, location.pathname, setParams, setDetailsData, handleResponse]
  );

  const getTableItemDetails = useCallback(
    async ({
      itemId,
      successMessage,
      leaveTableDetailsNotUpdated,
      updateTableRowParams,
      fromEditItem,
      valueInSearchField,
      rowData,
      leaveRowsChecked,
      selectedTableItemUrlKey: urlKey
    }: GetTableitemDetailsProps) => {
      if (updateTableRowParams && (isLoading || processingItemId === itemId)) {
        return;
      }

      try {
        if (updateTableRowParams && itemId) {
          setProcessingItemId(itemId);
        }

        setIsLoading(true);

        if (leaveTableDetailsNotUpdated || fromEditItem) {
          // refresh table and pass search params if they exist
          await refreshTableData(undefined, valueInSearchField);

          if (leaveRowsChecked) {
            setSelectedRows(selectedRows);
          } else {
            setSelectedRows([]);
          }
        }

        if (successMessage) {
          handleResponse(ALERT_STATUS.Success, successMessage);
        }

        // calling item details api is not necessary when adding new item,
        // since it does not impact details section content display
        if (itemId && !leaveTableDetailsNotUpdated) {
          await fetchAndUpdateItemDetails(
            itemId,
            !!updateTableRowParams,
            successMessage,
            rowData,
            urlKey
          );
        }
      } catch (error) {
        const err = error as AxiosError;
        if (err) {
          const errorMessage = err.message;
          handleResponse(ALERT_STATUS.Critical, errorMessage);
        }
      } finally {
        setIsLoading(false);
        setProcessingItemId(null);
      }
    },
    [
      selectedRows,
      refreshTableData,
      handleResponse,
      setSelectedRows,
      isLoading,
      processingItemId,
      fetchAndUpdateItemDetails
    ]
  );

  return { getTableItemDetails, isLoading };
};

export default useTableItemDetails;
